import React, { useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "../authContext";
import { Line, Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const SkillsProgress = () => {
  const { user } = useAuth();
  const [skillsData, setSkillsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!user || !user.userId) {
      setError("You need to log in to view your skills progress.");
      setLoading(false);
      return;
    }

    const fetchSkillsData = async () => {
      try {
        console.log(user);
        const response = await axios.get(
          `https://internsightbackend.onrender.com/api/skills/?userId=${user.userId}`
        );
        setSkillsData(response.data);
        setLoading(false);
      } catch (err) {
        setError("Failed to fetch data");
        setLoading(false);
      }
    };

    fetchSkillsData();
  }, [user]);

  const aggregatedData = skillsData.reduce((acc, skill) => {
    const date = new Date(skill.dateGained).toISOString().split("T")[0];
    if (acc[date]) {
      acc[date] += 1;
    } else {
      acc[date] = 1;
    }
    return acc;
  }, {});

  const sortedDates = Object.keys(aggregatedData).sort();

  let cumulativeSkills = 0;
  const cumulativeData = sortedDates.map((date) => {
    cumulativeSkills += aggregatedData[date];
    return cumulativeSkills;
  });

  const proficiencyData = skillsData.reduce((acc, skill) => {
    const proficiency = skill.proficiency;
    if (acc[proficiency]) {
      acc[proficiency] += 1;
    } else {
      acc[proficiency] = 1;
    }
    return acc;
  }, {});

  const proficiencyChartData = {
    labels: ["Beginner", "Intermediate", "Advanced"],
    datasets: [
      {
        label: "Skills by Proficiency",
        data: [
          proficiencyData[0] || 0,
          proficiencyData[1] || 0,
          proficiencyData[2] || 0,
        ],
        backgroundColor: [
          "rgba(255, 99, 132, 0.7)",
          "rgba(54, 162, 235, 0.7)",
          "rgba(75, 192, 192, 0.7)",
        ],
        borderColor: [
          "rgba(255, 99, 132, 1)",
          "rgba(54, 162, 235, 1)",
          "rgba(75, 192, 192, 1)",
        ],
        borderWidth: 1,
      },
    ],
  };

  const chartData = {
    labels: sortedDates,
    datasets: [
      {
        label: "Cumulative Skills Gained Over Time",
        data: cumulativeData,
        borderColor: "rgba(75, 192, 192, 1)",
        backgroundColor: "rgba(75, 192, 192, 0.2)",
        tension: 0.3,
        fill: true,
      },
    ],
  };

  return (
    <div className="skills-progress-container">
      <div className="header">
        <h1>Skills Progress Dashboard</h1>
        <p className="subtitle">Track your learning journey and skill development</p>
      </div>

      {loading ? (
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading your skills data...</p>
        </div>
      ) : error ? (
        <div className="error-message">
          <p>{error}</p>
        </div>
      ) : (
        <div className="charts-grid">
          <div className="chart-card">
            <div className="chart-header">
              <h2>Cumulative Skills Over Time</h2>
              <p className="chart-description">Track your skill growth timeline</p>
            </div>
            <div className="chart-wrapper">
              <Line
                data={chartData}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      position: 'top',
                      labels: {
                        font: {
                          size: 14
                        }
                      }
                    },
                    tooltip: {
                      backgroundColor: 'rgba(0,0,0,0.8)',
                      titleFont: {
                        size: 16
                      },
                      bodyFont: {
                        size: 14
                      }
                    }
                  },
                  scales: {
                    x: {
                      title: {
                        display: true,
                        text: "Dates",
                        font: {
                          size: 14,
                          weight: 'bold'
                        }
                      },
                      grid: {
                        display: false
                      }
                    },
                    y: {
                      title: {
                        display: true,
                        text: "Skills Gained",
                        font: {
                          size: 14,
                          weight: 'bold'
                        }
                      },
                      beginAtZero: true,
                      grid: {
                        color: 'rgba(0,0,0,0.05)'
                      }
                    },
                  },
                }}
              />
            </div>
          </div>

          <div className="chart-card">
            <div className="chart-header">
              <h2>Skills by Proficiency Level</h2>
              <p className="chart-description">Distribution of your skills by expertise</p>
            </div>
            <div className="chart-wrapper">
              <Bar
                data={proficiencyChartData}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  indexAxis: "y",
                  plugins: {
                    legend: {
                      position: 'top',
                      labels: {
                        font: {
                          size: 14
                        }
                      }
                    },
                    tooltip: {
                      backgroundColor: 'rgba(0,0,0,0.8)',
                      titleFont: {
                        size: 16
                      },
                      bodyFont: {
                        size: 14
                      }
                    }
                  },
                  scales: {
                    x: {
                      title: {
                        display: true,
                        text: "Number of Skills",
                        font: {
                          size: 14,
                          weight: 'bold'
                        }
                      },
                      beginAtZero: true,
                      grid: {
                        color: 'rgba(0,0,0,0.05)'
                      }
                    },
                    y: {
                      title: {
                        display: true,
                        text: "Proficiency Level",
                        font: {
                          size: 14,
                          weight: 'bold'
                        }
                      },
                      grid: {
                        display: false
                      }
                    },
                  },
                }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SkillsProgress;

// CSS Styles
const styles = `
.skills-progress-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  color: #333;
}

.header {
  text-align: center;
  margin-bottom: 2.5rem;
}

.header h1 {
  font-size: 2.5rem;
  color: #2c3e50;
  margin-bottom: 0.5rem;
  font-weight: 600;
}

.subtitle {
  font-size: 1.1rem;
  color: #7f8c8d;
  margin-top: 0;
}

.charts-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 2rem;
}

@media (min-width: 992px) {
  .charts-grid {
    grid-template-columns: 1fr 1fr;
  }
}

.chart-card {
  background: white;
  border-radius: 10px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  padding: 1.5rem;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.chart-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 6px 25px rgba(0, 0, 0, 0.12);
}

.chart-header {
  margin-bottom: 1.5rem;
}

.chart-header h2 {
  font-size: 1.5rem;
  color: #34495e;
  margin-bottom: 0.5rem;
  font-weight: 500;
}

.chart-description {
  color: #7f8c8d;
  font-size: 0.95rem;
  margin-top: 0;
}

.chart-wrapper {
  height: 350px;
  width: 100%;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 300px;
}

.spinner {
  border: 5px solid #f3f3f3;
  border-top: 5px solid #3498db;
  border-radius: 50%;
  width: 50px;
  height: 50px;
  animation: spin 1s linear infinite;
  margin-bottom: 1rem;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.error-message {
  background: #ffebee;
  color: #c62828;
  padding: 1.5rem;
  border-radius: 8px;
  text-align: center;
  font-weight: 500;
}
`;

// Inject styles
const styleSheet = document.createElement("style");
styleSheet.type = "text/css";
styleSheet.innerText = styles;
document.head.appendChild(styleSheet);